/**
 * LicenseManager Component
 *
 * Manages contractor licenses with:
 * - Add/Edit/Delete licenses
 * - Expiration tracking and alerts
 * - Document upload for license copies
 */

import { useState, useEffect } from 'react';
import { Timestamp, collection, doc, setDoc, deleteDoc, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import {
  ContractorLicense,
  LicenseType,
  LicenseStatus,
  LICENSE_TYPE_LABELS,
  isExpired,
  isExpiringSoon,
  daysUntilExpiration,
} from '@/types/contractor';

interface LicenseManagerProps {
  contractorId: string;
}

const US_STATES = [
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'AL', name: 'Alabama' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'CA', name: 'California' },
  // Add more as needed
];

export function LicenseManager({ contractorId }: LicenseManagerProps) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [licenses, setLicenses] = useState<ContractorLicense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<ContractorLicense | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    licenseType: '' as LicenseType,
    licenseNumber: '',
    qualifierName: '',
    state: 'FL',
    issuedDate: '',
    expirationDate: '',
    tradeType: '',
    county: '',
  });
  const [licenseFile, setLicenseFile] = useState<File | null>(null);

  // Fetch licenses from Firestore
  useEffect(() => {
    if (!contractorId) return;

    const licensesRef = collection(db, 'contractors', contractorId, 'licenses');
    const q = query(licensesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const licenseData: ContractorLicense[] = [];
      snapshot.forEach((doc) => {
        licenseData.push({ id: doc.id, ...doc.data() } as ContractorLicense);
      });
      setLicenses(licenseData);
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching licenses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load licenses',
        variant: 'destructive',
      });
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [contractorId, toast]);

  // Reset form
  const resetForm = () => {
    setFormData({
      licenseType: '' as LicenseType,
      licenseNumber: '',
      qualifierName: '',
      state: 'FL',
      issuedDate: '',
      expirationDate: '',
      tradeType: '',
      county: '',
    });
    setLicenseFile(null);
    setSelectedLicense(null);
  };

  // Open dialog for adding new license
  const handleAddLicense = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  // Open dialog for editing license
  const handleEditLicense = (license: ContractorLicense) => {
    setSelectedLicense(license);
    setFormData({
      licenseType: license.licenseType,
      licenseNumber: license.licenseNumber,
      qualifierName: license.qualifierName,
      state: license.state,
      issuedDate: license.issuedDate || '',
      expirationDate: license.expirationDate,
      tradeType: license.tradeType || '',
      county: license.county || '',
    });
    setIsDialogOpen(true);
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF, JPEG, or PNG file',
          variant: 'destructive',
        });
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Maximum file size is 10MB',
          variant: 'destructive',
        });
        return;
      }
      setLicenseFile(file);
    }
  };

  // Save license
  const handleSaveLicense = async () => {
    // Validation
    if (!formData.licenseType || !formData.licenseNumber || !formData.qualifierName || !formData.expirationDate) {
      toast({
        title: 'Missing required fields',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      let documentUrl = selectedLicense?.documentUrl;

      // Upload file if selected
      if (licenseFile) {
        const timestamp = Date.now();
        const storagePath = `licenses/${contractorId}/${timestamp}-${licenseFile.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, licenseFile);
        documentUrl = await getDownloadURL(storageRef);
      }

      // Determine status based on expiration date
      let status: LicenseStatus = 'active';
      if (isExpired(formData.expirationDate)) {
        status = 'expired';
      } else if (isExpiringSoon(formData.expirationDate, 60)) {
        status = 'pending_renewal';
      }

      const licenseData = {
        licenseType: formData.licenseType,
        licenseNumber: formData.licenseNumber.toUpperCase(),
        qualifierName: formData.qualifierName,
        state: formData.state,
        issuedDate: formData.issuedDate || null,
        expirationDate: formData.expirationDate,
        status,
        tradeType: formData.tradeType || null,
        county: formData.county || null,
        documentUrl: documentUrl || null,
        updatedAt: Timestamp.now(),
      };

      if (selectedLicense) {
        // Update existing license
        const licenseRef = doc(db, 'contractors', contractorId, 'licenses', selectedLicense.id);
        await setDoc(licenseRef, licenseData, { merge: true });
        toast({
          title: 'License updated',
          description: 'Your license has been updated successfully',
        });
      } else {
        // Create new license
        const newLicenseRef = doc(collection(db, 'contractors', contractorId, 'licenses'));
        await setDoc(newLicenseRef, {
          ...licenseData,
          id: newLicenseRef.id,
          createdAt: Timestamp.now(),
        });
        toast({
          title: 'License added',
          description: 'Your license has been added successfully',
        });
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving license:', error);
      toast({
        title: 'Error',
        description: 'Failed to save license. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete license
  const handleDeleteLicense = async () => {
    if (!selectedLicense) return;

    try {
      const licenseRef = doc(db, 'contractors', contractorId, 'licenses', selectedLicense.id);
      await deleteDoc(licenseRef);
      toast({
        title: 'License deleted',
        description: 'The license has been removed',
      });
      setIsDeleteDialogOpen(false);
      setSelectedLicense(null);
    } catch (error) {
      console.error('Error deleting license:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete license',
        variant: 'destructive',
      });
    }
  };

  // Get status badge
  const getStatusBadge = (license: ContractorLicense) => {
    if (isExpired(license.expirationDate)) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Expired
        </Badge>
      );
    }
    if (isExpiringSoon(license.expirationDate, 60)) {
      const days = daysUntilExpiration(license.expirationDate);
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-600 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Expires in {days} days
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-green-600 border-green-600 flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Active
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Licenses</CardTitle>
              <CardDescription>
                Manage your trade licenses and certifications
              </CardDescription>
            </div>
            <Button onClick={handleAddLicense} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add License
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {licenses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No licenses added yet</p>
              <p className="text-sm mt-1">Add your trade licenses to keep track of renewals</p>
            </div>
          ) : (
            <div className="space-y-4">
              {licenses.map((license) => (
                <div
                  key={license.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">
                        {LICENSE_TYPE_LABELS[license.licenseType] || license.licenseType}
                      </span>
                      {getStatusBadge(license)}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-0.5">
                      <p>
                        <span className="font-medium">License #:</span> {license.licenseNumber}
                      </p>
                      <p>
                        <span className="font-medium">Qualifier:</span> {license.qualifierName}
                      </p>
                      <p>
                        <span className="font-medium">State:</span> {license.state}
                        {license.county && ` | County: ${license.county}`}
                      </p>
                      <p>
                        <span className="font-medium">Expires:</span>{' '}
                        {new Date(license.expirationDate).toLocaleDateString()}
                      </p>
                    </div>
                    {license.documentUrl && (
                      <a
                        href={license.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary mt-2 hover:underline"
                      >
                        <FileText className="h-3 w-3" />
                        View Document
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditLicense(license)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setSelectedLicense(license);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit License Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedLicense ? 'Edit License' : 'Add License'}
            </DialogTitle>
            <DialogDescription>
              {selectedLicense
                ? 'Update your license information'
                : 'Add a new trade license to your profile'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="licenseType">License Type *</Label>
              <Select
                value={formData.licenseType}
                onValueChange={(value) =>
                  setFormData({ ...formData, licenseType: value as LicenseType })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select license type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LICENSE_TYPE_LABELS).map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {code} - {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="licenseNumber">License Number *</Label>
              <Input
                id="licenseNumber"
                value={formData.licenseNumber}
                onChange={(e) =>
                  setFormData({ ...formData, licenseNumber: e.target.value })
                }
                placeholder="e.g., EC13012345"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="qualifierName">Qualifier Name *</Label>
              <Input
                id="qualifierName"
                value={formData.qualifierName}
                onChange={(e) =>
                  setFormData({ ...formData, qualifierName: e.target.value })
                }
                placeholder="Name on license"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) =>
                    setFormData({ ...formData, state: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.code} - {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="county">County</Label>
                <Input
                  id="county"
                  value={formData.county}
                  onChange={(e) =>
                    setFormData({ ...formData, county: e.target.value })
                  }
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="issuedDate">Issue Date</Label>
                <Input
                  id="issuedDate"
                  type="date"
                  value={formData.issuedDate}
                  onChange={(e) =>
                    setFormData({ ...formData, issuedDate: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expirationDate">Expiration Date *</Label>
                <Input
                  id="expirationDate"
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) =>
                    setFormData({ ...formData, expirationDate: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="licenseFile">License Document</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="licenseFile"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="flex-1"
                />
              </div>
              {licenseFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {licenseFile.name}
                </p>
              )}
              {selectedLicense?.documentUrl && !licenseFile && (
                <p className="text-sm text-muted-foreground">
                  Current document on file
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLicense} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {licenseFile && <Upload className="h-4 w-4 mr-2" />}
                  {selectedLicense ? 'Update' : 'Add'} License
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete License?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this license from your profile.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLicense}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
